import {
  SAVE_FLOW,
  GET_FLOWS,
  MODIFIED_SELECTED_FLOW,
  SET_SAVED,
  CREATE_FLOW,
  DELETE_FLOW,
  TOGGLE_LOADING,
  PREVIEW_REQUEST,
  RESOLVE_REQUEST,
  SET_URL,
  SET_RENDER,
  SAVE_FLOW_ONLY,
  SET_FLOW_EDIT_LOCK,
  SET_FLOW_RUNNER_STATUS,
} from "./types";
import { getBaseURL } from "../../env/stage";
import { del, get, post } from "../../common/modules/http.client";
import { Flow } from "../../common/interfaces/flow/Flow";
import { addMessage } from "./messageActions";
import { readError } from "../../common/utils/responseErrorReader";
const APP_URL = getBaseURL() + "/flows";
const KEY_GEN_URL = getBaseURL() + "/keypair";
const FLOW_STATUS_URL = getBaseURL() + "/flow/status";
const STOP_FLOW_URL = getBaseURL() + "/flow/stop";

interface FlowRunStatus {
  flowExecutionId: string;
  status: string;
  startedAt: string;
  endedAt?: string;
  message?: string;
  errorMessage?: string;
  updatedAt: string;
  stats?: {
    actions: Array<{
      actionId: string;
      actionName?: string;
      runs: number;
      totalExecutionTimeMs: number;
      averageExecutionTimeMs: number;
      totalResponseSizeBytes: number;
    }>;
    totalResponseSizeBytes: number;
  };
}

/**
 * Get all applications
 */
export const getFlows = (lastLoadedFlowId: string) => (dispatch: any) => {
  get(APP_URL + "?lastLoadedFlowId=" + lastLoadedFlowId)
    .then((res) => {
      dispatch({
        type: GET_FLOWS,
        payload: res,
      });
      dispatch({
        type: TOGGLE_LOADING,
        payload: { status: false, loadingMessage: "loading..." },
      });
    })
    .catch((err) => {
      console.log("err", err);
      dispatch(addMessage("Error getting integrations", err.statusText, "error"));
    });
};
export const selectFlow = (flow: Flow) => (dispatch: any) => {
  dispatch({
    type: MODIFIED_SELECTED_FLOW,
    payload: JSON.parse(JSON.stringify(flow)),
  });
};
export const setEditorSaved = (saved: Boolean) => (dispatch: any) => {
  dispatch({
    type: SET_SAVED,
    payload: saved,
  });
};
export const saveFlow =
  (flow: Flow, selectFlow = true) =>
  async (dispatch: any, getState: any) => {
    if (!flow) {
      dispatch(addMessage("Error saving integration", "Flow has no data it is empty!", "error"));
      return;
    }

    const EVENT = selectFlow ? SAVE_FLOW : SAVE_FLOW_ONLY;

    //set current user
    const jiraUser = getState()?.jira?.jiraUser;
    const clonedFlow = { ...flow, metadata: { ...(flow.metadata || {}) } };
    clonedFlow.metadata.updatedBy = jiraUser?.accountId ?? "";

    //update the flow
    try {
      const res = await post(APP_URL, clonedFlow);

      if (res?.error) {
        dispatch(addMessage("Error saving integration", res?.error, "error"));
      } else {
        dispatch({
          type: EVENT,
          payload: res,
        });
        dispatch({
          type: TOGGLE_LOADING,
          payload: { status: false, loadingMessage: "loading..." },
        });
      }
    } catch (err) {
      //console.log("Error in saveFlow:", err);
      if (err instanceof Response) {
        const errorText = await readError(err);
        dispatch(addMessage("Error saving integration", errorText, "error"));
      } else {
        dispatch(addMessage("Error saving integration", "An unknown error occurred", "error"));
      }
    }
  };

export const createFlow = (flow: Flow) => (dispatch: any, getState: any) => {
  if (!flow) {
    dispatch(addMessage("Error saving integration", "Flow has no data it is empty!", "error"));
    return;
  }

  //set current user
  const jiraUser = getState()?.jira?.jiraUser;
  (flow.metadata ??= {}).createdBy = jiraUser?.accountId ?? "";
  (flow.metadata ??= {}).updatedBy = jiraUser?.accountId ?? "";

  //create the flow
  post(APP_URL, flow)
    .then((res) => {
      dispatch({
        type: CREATE_FLOW,
        payload: res,
      });
      dispatch({
        type: TOGGLE_LOADING,
        payload: { status: false, loadingMessage: "loading..." },
      });
    })
    .catch((err) => {
      console.log("err", err);
      dispatch(addMessage("Error creating integration", err.statusText, "error"));
    });
};
export const deleteFlow = (flow: Flow) => (dispatch: any) => {
  del(APP_URL + "/" + flow.id)
    .then((res) => {
      dispatch({
        type: DELETE_FLOW,
        payload: flow,
      });
    })
    .catch((err) => {
      console.log("err", err);
      dispatch(addMessage("Error deleting integration", err.statusText, "error"));
    });
};
export const showPreview = (isVisible: boolean) => (dispatch: any) => {
  dispatch({
    type: PREVIEW_REQUEST,
    payload: isVisible,
  });
};
export const resolveRequest = (requestData: any) => (dispatch: any) => {
  dispatch({
    type: RESOLVE_REQUEST,
    payload: requestData,
  });
};
export const selectCurrentURL = (url: string) => (dispatch: any) => {
  dispatch({
    type: SET_URL,
    payload: url,
  });
};

export const rerender = () => (dispatch: any) => {
  dispatch({
    type: SET_RENDER,
    payload: Date.now(),
  });
};

export const generateJWTTriggerKeyPair = (flow: Flow) => async (dispatch: any) => {
  try {
    const res = await post(KEY_GEN_URL, flow);

    if (res?.error) {
      dispatch(addMessage("Error saving integration", res.error, "error"));
    } else {
      dispatch({
        type: SAVE_FLOW,
        payload: res,
      });
      dispatch({
        type: TOGGLE_LOADING,
        payload: { status: false, loadingMessage: "loading..." },
      });
    }
  } catch (err) {
    if (err instanceof Response) {
      const errorText = await readError(err);
      dispatch(addMessage("Error saving integration", errorText, "error"));
    } else {
      dispatch(addMessage("Error saving integration", "An unknown error occurred", "error"));
    }
  }
};

export const fetchFlowRunnerStatus = (flowId: string) => async (dispatch: any) => {
  if (!flowId) {
    return;
  }

  let statuses: FlowRunStatus[] = [];
  let errorMessage: string | null = null;

  try {
    const res = await get(`${FLOW_STATUS_URL}?flowId=${encodeURIComponent(flowId)}`);
    statuses = Array.isArray(res) ? res : [];
  } catch (err) {
    if (err instanceof Response) {
      errorMessage = await readError(err);
    } else {
      errorMessage = err && typeof err === "object" && "message" in err ? (err as Error).message : "An unknown error occurred";
    }
  }

  dispatch({
    type: SET_FLOW_RUNNER_STATUS,
    payload: {
      flowId,
      statuses,
    },
  });

  if (errorMessage) {
    dispatch(addMessage("Error getting run history", errorMessage, "error"));
  }
};

export const stopFlowExecution = (flow: Flow) => async (dispatch: any) => {
  if (!flow?.id) {
    dispatch(addMessage("Error stopping integration", "Flow id is missing", "error"));
    return;
  }

  try {
    await post(STOP_FLOW_URL, { id: flow.id, enabled: false });
  } catch (err) {
    if (err instanceof Response) {
      const errorText = await readError(err);
      dispatch(addMessage("Error stopping integration", errorText, "error"));
    } else {
      const fallbackError = err && typeof err === "object" && "message" in err ? (err as Error).message : "An unknown error occurred";
      dispatch(addMessage("Error stopping integration", fallbackError, "error"));
    }
  }
};
export const setFlowLock = (lockFlow: boolean) => (dispatch: any) => {
  dispatch({
    type: SET_FLOW_EDIT_LOCK,
    payload: lockFlow,
  });
};
